import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { register } = useAuth();
    const token = params.get("token") || "";
    const [invitation, setInvitation] = useState(null);
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(Boolean(token));

    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const res = await api.get(`/auth/invitations/${encodeURIComponent(token)}`);
                setInvitation(res.data);
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Invitation could not be loaded");
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const accept = async (e) => {
        e.preventDefault();
        try {
            await register({ token, password });
            toast.success("Account created");
            navigate("/");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not accept invitation");
        }
    };

    const inviteValid = invitation?.status === "pending";

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#F9FAFB]">
            <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8" data-testid="register-invite-only-panel">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                    <Lock size={14} /> Private estate access
                </div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mt-2">
                    {token ? "Accept invitation" : "Invitation required"}
                </h1>
                {!token && (
                    <>
                        <p className="text-sm text-slate-600 mt-2">
                            This portal contains resident cases, title-deed evidence, and estate documents. Accounts are created by
                            the estate admin or management company so each resident is linked to the correct property.
                        </p>
                        <div className="border border-slate-200 bg-slate-50 rounded-lg p-4 mt-5 text-sm text-slate-700">
                            <div className="font-medium text-slate-900">Need access?</div>
                            <p className="mt-1">
                                Contact the estate admin and ask them to send an invitation link to your email address.
                            </p>
                        </div>
                    </>
                )}

                {token && loading && <div className="text-sm text-slate-500 mt-4">Loading invitation...</div>}

                {token && !loading && invitation && (
                    <div className="border border-slate-200 bg-slate-50 rounded-lg p-4 mt-5 text-sm text-slate-700" data-testid="invitation-summary">
                        <div className="font-medium text-slate-900">{invitation.full_name}</div>
                        <div>{invitation.email}</div>
                        <div className="text-xs text-slate-500 mt-1">
                            Role: {invitation.role.replace("_", " ")} · Expires {new Date(invitation.expires_at).toLocaleDateString()}
                        </div>
                        {!inviteValid && <div className="text-sm text-rose-700 mt-2">This invitation is {invitation.status}.</div>}
                    </div>
                )}

                {token && inviteValid && (
                    <form onSubmit={accept} className="space-y-4 mt-5" data-testid="accept-invitation-form">
                        <div className="space-y-1.5">
                            <Label>Password</Label>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                data-testid="accept-invite-password"
                            />
                        </div>
                        <Button type="submit" className="w-full bg-slate-700 hover:bg-slate-800 text-white" data-testid="accept-invite-button">
                            Create account
                        </Button>
                    </form>
                )}

                <Button asChild variant={token ? "outline" : "default"} className={`w-full mt-6 ${token ? "" : "bg-slate-700 hover:bg-slate-800 text-white"}`} data-testid="register-login-link">
                    <Link to="/login">
                        <Mail size={16} className="mr-2" /> Back to sign in
                    </Link>
                </Button>
            </div>
        </div>
    );
}
